import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createFloorMap,
  deleteFloorMap,
  getFloorMaps,
} from "../../redux/floorMap/floorMapSlice";
import { toast } from "react-toastify";
import { getImageUrlHelper, truncateText } from "../../helper/helper";
import useDebounce from "../../hook/useDebounce";
import { processFileUpload, FILE_TYPES } from "../../utils/fileUploadHelper";
import { useLocationFilter } from "../../context/LocationContext";
import LocationSelectComponent from "../../components/common/LocationSelectComponent";
import PaginationHelper from "../../helper/PaginationHelper";

const DESC_MAX = 255;
const TITLE_MAX = 255;

const FloorMapLayer = () => {
  const dispatch = useDispatch();
  const { selectedLocation } = useLocationFilter();

  const { floorMaps = [], total = 0 } = useSelector(
    (state) => state.floorMap
  );
  const { locations = [] } = useSelector((state) => state.eventLocation);

  const [entries, setEntries] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 1000);

  const [formData, setFormData] = useState({
    location: null,
    title: "",
    description: "",
    file: null,
  });

  const [preview, setPreview] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    dispatch(
      getFloorMaps({
        page: currentPage,
        limit: entries,
        search: debouncedSearch,
        locationId: selectedLocation?.value || "",
      })
    );
  }, [dispatch, currentPage, entries, debouncedSearch, selectedLocation]);

  const totalPages = Math.ceil(total / entries);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      ...FILE_TYPES.IMAGE,
      ...FILE_TYPES.PDF,
    ];

    const processedFile = await processFileUpload(file, {
      allowedTypes,
    });

    if (!processedFile) return;

    setFormData({ ...formData, file: processedFile });

    if (processedFile.type.startsWith("image/")) {
      setPreview(URL.createObjectURL(processedFile));
    } else {
      setPreview(null);
    }
  };

  const handleSubmit = () => {
    const { locationId, title, description, file } = formData;

    if (!formData.location) {
      toast.error("Location is required");
      return;
    }

    if (!title || !title.trim()) {
      toast.error("Title is required");
      return;
    }

    if (title.length > TITLE_MAX) {
      toast.error(`Title max ${TITLE_MAX} characters`);
      return;
    }

    if (description && description.length > DESC_MAX) {
      toast.error(`Description max ${DESC_MAX} characters`);
      return;
    }

    if (!file) {
      toast.error("File is required");
      return;
    }

    const fd = new FormData();
    fd.append("locationId", formData.location.value);
    fd.append("title", title);
    fd.append("description", description);
    fd.append("file", file);

    dispatch(createFloorMap(fd))
      .unwrap()
      .then((res) => {
        toast.success(res?.message || "Created successfully");

        dispatch(getFloorMaps({
          page: currentPage,
          limit: entries,
          search: debouncedSearch,
          locationId: selectedLocation?.value || "",
        }));

        document.querySelector("#floorMapModal .btn-close")?.click();

        setPreview(null);
        setFormData({
          locationId: "",
          title: "",
          description: "",
          file: null,
        });
      })
      .catch((err) => {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong";

        toast.error(message);
      });
  };

  return (
    <>
      <div className="card h-100 p-0 radius-12">

        {/* HEADER */}
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between flex-wrap gap-3">

          <div className="d-flex align-items-center gap-3 flex-wrap">

            <span className="text-md fw-medium text-secondary-light">
              Show
            </span>

            <select
              className="form-select form-select-sm w-auto"
              value={entries}
              onChange={(e) => {
                setEntries(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="150">150</option>
              <option value="200">200</option>
            </select>

            {/* SEARCH */}
            <input
              type="text"
              className="form-control w-auto"
              placeholder="Search title..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />

          </div>

          <button
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            data-bs-toggle="modal"
            data-bs-target="#floorMapModal"

          >
            <Icon icon="ic:baseline-plus" />
            Add Floor Map
          </button>

        </div>

        {/* TABLE */}
        <div className="card-body p-12">

          <div className="table-responsive">

            <table className="table bordered-table">

              <thead>
                <tr>
                  <th>S.L</th>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Location</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {floorMaps.length > 0 ? (

                  floorMaps.map((f, index) => (
                    <tr key={f.id}>

                      <td>
                        {(currentPage - 1) * entries + index + 1}
                      </td>

                      <td>
                        <img
                          src={getImageUrlHelper(f.path)}
                          className="w-60-px h-60-px object-fit-cover radius-8"
                        />
                      </td>

                      <td>{truncateText(f.title) || "N/A"}</td>
                      <td>{truncateText(f.description) || "N/A"}</td>

                      <td>{truncateText(f?.event?.name) || "N/A"}</td>

                      <td className="text-center">
                        <button
                          className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          data-bs-toggle="modal"
                          data-bs-target="#deleteModal"
                          onClick={() => setDeleteId(f.id)}
                        >
                          <Icon icon="fluent:delete-24-regular" />
                        </button>
                      </td>

                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No Floor Maps found
                    </td>
                  </tr>
                )}

              </tbody>

            </table>

          </div>

          {/* PAGINATION */}
          <PaginationHelper
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={total}
            pageSize={entries}
          />

        </div>
      </div>

      {/* CREATE MODAL */}
      <div className="modal fade" id="floorMapModal">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content radius-16">

            <div className="modal-header">
              <h5>Add Floor Map</h5>
              <button className="btn-close" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body">
              <div className="row">

                {/* LOCATION */}
                <div className="col-md-6 mb-3">
                  <LocationSelectComponent
                    label="Location"
                    required
                    value={formData.location}
                    onChange={(val) =>
                      setFormData({ ...formData, location: val })
                    }
                  />
                </div>

                {/* TITLE */}
                <div className="col-md-6 mb-3">
                  <label>Title <span className="text-danger">*</span></label>
                  <input
                    className="form-control"
                    maxLength={255}
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                  />
                  {/* <small className="text-muted">Max 255 chars</small> */}
                </div>

                {/* DESCRIPTION */}
                <div className="col-md-6 mb-3">
                  <label>
                    Description <small className="fst-italic">(Max 255 chars)</small>
                  </label>
                  <textarea
                    className="form-control"
                    rows={5}
                    maxLength={255}
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        description: e.target.value,
                      })
                    }
                  />
                </div>

                {/* FILE UPLOAD */}
                <div className="col-md-6 mb-3">
                  <label>Upload File <span className="text-danger">*</span></label>

                  <label
                    htmlFor="floorUpload"
                    className="border radius-8 border-dashed d-flex align-items-center justify-content-center"
                    style={{ height: "150px", cursor: "pointer" }}
                  >
                    {preview ? (
                      <img
                        src={preview}
                        className="w-100 h-100 object-fit-contain radius-8"
                      />
                    ) : formData.file?.type === "application/pdf" ? (
                      <div className="text-center">
                        <Icon icon="mdi:file-pdf-box" width="40" color="red" />
                        <p>PDF Selected</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Icon icon="solar:camera-outline" />
                        <p><small>(Only image & pdf allowed)</small></p>
                      </div>
                    )}
                  </label>

                  <input
                    id="floorUpload"
                    type="file"
                    hidden
                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                    onChange={handleFileChange}
                  />
                </div>

              </div>

              <div className="text-end">
                <button className="btn btn-primary" onClick={handleSubmit}>
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DELETE MODAL */}
      <div className="modal fade" id="deleteModal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 text-center p-24">

            <p>Are you sure you want to delete this floor map?</p>

            <div className="d-flex justify-content-center gap-3 mt-3">
              <button
                className="btn btn-light"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>

              <button
                className="btn btn-danger"
                onClick={() => {
                  dispatch(deleteFloorMap(deleteId))
                    .unwrap()
                    .then(() => {
                      toast.success("Deleted");

                      dispatch(
                        getFloorMaps({
                          page: currentPage,
                          limit: entries,
                          search: debouncedSearch,
                          locationId: selectedLocation?.value || "",
                        })
                      );

                      document
                        .querySelector("#deleteModal .btn-close")
                        ?.click();
                    });
                }}
              >
                Delete
              </button>
            </div>

          </div>
        </div>
      </div>

    </>
  );
};

export default FloorMapLayer;